import { forwardRef, useCallback, useEffect, useId, useRef, useState } from 'react'

import { cn } from '@/lib/utils'
import { FX_RAIL_ITEMS, FxAppRail, type FxNavItem, type FxNavKey } from './FxAppRail'
import { FX_BOTTOM_NAV_ITEMS, FxBottomNav } from './FxBottomNav'
import { FxIcon } from './FxIcon'
import { FxTopBar } from './FxTopBar'

/**
 * The navigation frame every signed-in screen sits in, so that no screen
 * reimplements it: the 80px rail from 992px up, a top bar with a hamburger
 * and a slide-over below that, and the phone's bottom nav below 576px.
 *
 * The design system has no shell component — the README states the behaviour
 * ("below 992px the rail becomes a hamburger in a top bar and panels stack;
 * on phones a BottomNav carries …") and the artboards draw both ends of it.
 * This is that behaviour in one place. A screen renders
 * `<FxNavShell active="home" …>{content}</FxNavShell>` and lays out its own
 * content inside; the shell adds the desktop page gutter (`pl-17`) but no
 * vertical padding, because pages differ there.
 *
 * The drawer is the one place here that manages focus: it takes focus when it
 * opens, keeps Tab inside itself (it claims `aria-modal`, and a modal that
 * leaks focus into the page it covers is not one), closes on Escape and on a
 * backdrop click, and gives focus back to the hamburger.
 *
 * `<main>` carries `MAIN_ID` and `tabIndex={-1}` so the skip link — and an
 * app that moves focus on a route change — has somewhere to land.
 */

/** The skip link's target, and the id an app focuses after a navigation. */
export const MAIN_ID = 'hauptinhalt'

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
export interface FxNavShellProps extends React.HTMLAttributes<HTMLDivElement> {
  active?: FxNavKey | string
  items?: FxNavItem[]
  /** Items the rail puts below the spacer (Einstellungen on the artboards). */
  footerItems?: FxNavItem[]
  /**
   * The phone's five slots. The rail's destinations and the bottom nav's are
   * not the same list — the bottom nav has room for five and puts the create
   * action in the middle — so an app that changes one has to be able to
   * change the other.
   */
  bottomItems?: FxNavItem[]
  /** Replaces the rail's built-in account button — see `FxAppRail`. */
  accountSlot?: React.ReactNode
  onNavigate?: (key: string) => void
  onCreate?: () => void
  initials?: string
  /** An avatar element for the rail and the drawer (e.g. `FxAvatar`). */
  avatar?: React.ReactNode
  /** Page actions in the top bar, left of the hamburger. */
  topBarActions?: React.ReactNode
  children?: React.ReactNode
}

export const FxNavShell = forwardRef<HTMLDivElement, FxNavShellProps>(
  (
    {
      active = 'home',
      items = FX_RAIL_ITEMS,
      footerItems,
      bottomItems = FX_BOTTOM_NAV_ITEMS,
      accountSlot,
      onNavigate,
      onCreate,
      initials,
      avatar,
      topBarActions,
      children,
      className,
      ...props
    },
    ref,
  ) => {
    const [menuOpen, setMenuOpen] = useState(false)
    const drawerId = useId()
    const menuButton = useRef<HTMLElement | null>(null)

    const closeMenu = useCallback(() => {
      setMenuOpen(false)
      // Focus goes back where it came from, or it would land on <body> and
      // the next Tab would start the page over.
      menuButton.current?.focus()
    }, [])

    const navigate = useCallback(
      (key: string) => {
        setMenuOpen(false)
        onNavigate?.(key)
      },
      [onNavigate],
    )

    const create = useCallback(() => {
      setMenuOpen(false)
      onCreate?.()
    }, [onCreate])

    return (
      <div
        ref={ref}
        className={cn('flex min-h-screen flex-col md:flex-row', className)}
        {...props}
      >
        {/* The first focusable thing on every screen. Without it a keyboard
            user Tabs through the whole rail (and the drawer's trigger, and
            the utility row) on every single navigation before reaching the
            page they asked for. */}
        <a
          href={`#${MAIN_ID}`}
          className="sr-only rounded-md bg-surface px-4 py-2 text-body-strong text-primary-text shadow-overlay focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50"
        >
          Zum Inhalt springen
        </a>
        <FxAppRail
          className="hidden md:sticky md:top-0 md:flex md:h-screen"
          active={active}
          items={items}
          footerItems={footerItems}
          initials={initials}
          avatar={avatar}
          accountSlot={accountSlot}
          onNavigate={onNavigate}
          onCreate={onCreate}
        />

        <FxTopBar
          className="sticky top-0 z-30 md:hidden"
          actions={topBarActions}
          menuOpen={menuOpen}
          menuControls={drawerId}
          onMenu={(event) => {
            menuButton.current = event.currentTarget
            setMenuOpen(true)
          }}
        />

        <main id={MAIN_ID} tabIndex={-1} className="min-w-0 flex-1 outline-none md:pl-17">
          {children}
        </main>

        <FxBottomNav
          className="sticky bottom-0 z-30 xs:hidden"
          active={active}
          items={bottomItems}
          onNavigate={(key) => (key === 'create' ? create() : navigate(key))}
        />

        {menuOpen && (
          <NavDrawer
            id={drawerId}
            active={active}
            items={[...items, ...(footerItems ?? [])]}
            onNavigate={navigate}
            onCreate={onCreate ? create : undefined}
            onClose={closeMenu}
          />
        )}
      </div>
    )
  },
)
FxNavShell.displayName = 'FxNavShell'

/**
 * The slide-over the hamburger opens: the same destinations as the rail, with
 * their names, because a drawer has room for words the rail does not.
 *
 * The backdrop is black at 55%, which is the design system's `scrim` value.
 * It cannot be the `shadow-scrim` token: that dims the page *around* an
 * element, which is what a centred dialog needs and a full-height panel at
 * the edge does not.
 */
function NavDrawer({
  id,
  active,
  items,
  onNavigate,
  onCreate,
  onClose,
}: {
  id: string
  active: string
  items: FxNavItem[]
  onNavigate: (key: string) => void
  onCreate?: () => void
  onClose: () => void
}) {
  const panel = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const node = panel.current
    node?.focus()
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
        return
      }
      // Tab stays in the drawer. Nothing outside it is inert, so without
      // this the tab order walks straight out of an `aria-modal` dialog into
      // the page it is covering.
      if (event.key !== 'Tab' || !node) return
      const items = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE))
      if (items.length === 0) return
      const first = items[0]
      const last = items[items.length - 1]
      const current = document.activeElement
      if (!event.shiftKey && (current === last || current === node)) {
        event.preventDefault()
        first.focus()
      } else if (event.shiftKey && (current === first || current === node)) {
        event.preventDefault()
        last.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  return (
    <div className="fixed inset-0 z-40 flex md:hidden">
      {/* A plain element, not a button: as a full-screen button it was the
          first stop in the tab order and a second control with the same
          name as the close button inside the drawer. Clicking it still
          closes the drawer. */}
      <div aria-hidden className="absolute inset-0 bg-black/55" onMouseDown={onClose} />
      <div
        id={id}
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label="Hauptnavigation"
        tabIndex={-1}
        className="relative ml-auto flex h-full w-[280px] max-w-[80%] flex-col gap-2 bg-surface p-5 shadow-overlay"
      >
        <button
          type="button"
          aria-label="Menü schließen"
          className="mb-2 flex h-11 w-11 items-center justify-center self-end rounded-sm text-text"
          onClick={onClose}
        >
          <FxIcon name="close" size={28} />
        </button>

        {onCreate && (
          <DrawerItem
            item={{ key: 'create', icon: 'add', label: 'Neues Musterformular' }}
            accent
            onSelect={onCreate}
          />
        )}
        {items.map((item) => (
          <DrawerItem
            key={item.key}
            item={item}
            active={active === item.key}
            onSelect={() => onNavigate(item.key)}
          />
        ))}
        <span className="flex-1" />
        <hr className="border-0 border-t-2 border-divider" />
        <DrawerItem
          item={{ key: 'account', icon: 'person', label: 'Account' }}
          active={active === 'account'}
          onSelect={() => onNavigate('account')}
        />
      </div>
    </div>
  )
}

function DrawerItem({
  item,
  active = false,
  accent = false,
  onSelect,
}: {
  item: FxNavItem
  active?: boolean
  accent?: boolean
  onSelect: () => void
}) {
  const className = cn(
    'flex h-control items-center gap-4 rounded-sm px-4 text-body-strong text-text no-underline transition-colors',
    accent && 'text-primary-text',
    active && 'bg-primary-strong text-on-primary shadow-raised',
  )
  const content = (
    <>
      <FxIcon name={item.icon} size={24} filled={!accent} />
      {item.label}
    </>
  )
  return item.href ? (
    <a
      href={item.href}
      className={className}
      aria-current={active ? 'page' : undefined}
      onClick={onSelect}
    >
      {content}
    </a>
  ) : (
    <button
      type="button"
      className={className}
      aria-current={active ? 'page' : undefined}
      onClick={onSelect}
    >
      {content}
    </button>
  )
}
